import { Injectable, inject } from "@angular/core";
import { ErrorMessage, Utilities } from "@tylertech/shared-module";
import
{
    MetadataVerification,
    MetadataCollectionVerification,
    RepositoryDataCollection,
    IViewMetadata,
    IMetadataAction,
    ClassMetadata,
} from "@tylertech/tyler-api-types";
import { ArrayHelpers, ObjectHelpers } from "../utilities";
import { AugmentedModelObject, AugmentedPropertyMetadata } from "../interfaces";
import { MetadataViewService } from "./metadataViewService";
import { AbstractControl } from "@angular/forms";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { MetadataService } from "./metadata.service";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";

/**
 * We can't rely on the Typescript Generator for this type because it lives in Api.Core.
 * But this is actually a better description of the C# class since it's current implementation
 * makes the MetadataVerification and MetadataCollectionVerification properties mutually exclusive.
 */
export type AdditionalInfoObject = MetadataVerification | MetadataCollectionVerification;
export type keyValue = {
    fieldName: string,
    messages: string[];
};

export const serverVerificationKey = "serverVerification";

@Injectable({ providedIn: "root" })
export class MetadataErrorService
{
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $metadataService = inject(MetadataService);

    private readonly log = getLoggerFor("MetadataErrorService");

    // TODO - this service needs a way to track metadataVerifications in simple cases where we don't require RepositoryDataCollection; i.e. naked metadataPanels.
    @LogInvocation(LogLevel.DEBUG)
    setErrors(errorInfo: AdditionalInfoObject, repositoryFormData: RepositoryDataCollection): void
    {
        if (!repositoryFormData)
        {
            return;
        }

        this.clearErrors(repositoryFormData);

        let verifications: MetadataVerification[];
        if ("Verifications" in errorInfo)
        {
            verifications = errorInfo.Verifications;
        }
        else if ("FieldErrors" in errorInfo)
        {
            verifications = [errorInfo];
        }
        else
        {
            throw new Error(`Unexpected errorInfo; was: ${JSON.stringify(errorInfo)}`);
        }
        for (const verification of verifications)
        {
            let entity: AugmentedModelObject;
            if (repositoryFormData.Type === verification.Type)
            {
                entity = repositoryFormData.Entity;
            }
            else if (verification.Type in repositoryFormData.OtherEntities)
            {
                const entityList = repositoryFormData.OtherEntities[verification.Type];
                entity = entityList[verification.Index];
            }

            if (entity)
            {
                this.updateModel(entity, verification);
            }
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    protected clearErrors(repositoryFormData: RepositoryDataCollection): void
    {
        this.clearErrorsFromModel(repositoryFormData.Entity);
        Object.values(repositoryFormData.OtherEntities)
            .forEach(entities =>
            {
                entities.forEach(entity =>
                {
                    this.clearErrorsFromModel(entity);
                });
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    protected clearErrorsFromModel(model: AugmentedModelObject): void
    {
        if (!model || !model["$isEntityWrapper"])
            return;

        if (model.$errors)
            delete model.$errors;

        for (const field in model)
        {
            if (!field.startsWith("$") && !!model[field])
            {
                const value = model[field];
                if (Array.isArray(value)
                    && value.length > 0
                    && value[0]
                    && value[0]["$isEntityWrapper"])
                {
                    value.forEach((item) =>
                    {this.clearErrorsFromModel(item);});
                }

                if (value && value["$isEntityWrapper"])
                    this.clearErrorsFromModel(value);
            }
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    getErrorList(errorInfo: AdditionalInfoObject): Array<any>
    {
        const errors: Array<string | Array<string>> = [];
        let verifications: MetadataVerification[];
        if ("Verifications" in errorInfo)
        {
            verifications = errorInfo.Verifications;
        }
        else if ("FieldErrors" in errorInfo)
        {
            verifications = [errorInfo];
        }
        else if ("error" in errorInfo)
        {
            this.log.warning(`getErrorList | weird errorInfo type...`, { errorInfo, typeof: typeof errorInfo, });

            if ("AdditionalInfo" in errorInfo["error"])
            {
                return this.getErrorList(JSON.parse(errorInfo["error"]["AdditionalInfo"]));
            }
            verifications = [];
            errors.push(errorInfo["error"]["Message"]);
        }
        else
        {
            throw new Error(`Unexpected errorInfo; was: ${JSON.stringify(errorInfo)}`);
        }

        for (const verification of verifications)
        {
            for (const err in verification.FieldErrors)
            {
                let skipPush = false;
                const fieldError = verification.FieldErrors[err];
                if (Array.isArray(fieldError) && fieldError.length === 1)
                {
                    skipPush = errors.map(
                        e =>
                        {
                            return Array.isArray(e) && e.length === 1
                                ? e[0]
                                : e;
                        })
                        .indexOf(fieldError[0]) > -1;
                }

                if (!skipPush)
                    errors.push(fieldError);
            }
            if (!Utilities.IsNullOrUndefinedOrEmptyObject(verification.SubPropertyErrors))
            {
                for (const field of Object.keys(verification.SubPropertyErrors))
                {
                    const subVerifications = verification.SubPropertyErrors[field];
                    const collection: MetadataCollectionVerification = {
                        Verifications: subVerifications,
                        IsValid: undefined,
                    };
                    errors.push(...this.getErrorList(collection));
                }
            }
        }

        const list = errors.flat();
        // TODO - what is this filter for? my best guess is removing duplicate strings?
        return list.filter((item, pos) => list.indexOf(item) === pos);
    }

    @LogInvocation(LogLevel.DEBUG)
    protected updateModel(model: AugmentedModelObject, verification: MetadataVerification): void
    {
        model.$errors = {};
        for (const field of Object.keys(verification.FieldErrors))
        {
            const message = verification.FieldErrors[field].join("\n");
            ObjectHelpers.safeAccess(model.$errors, field, {})[serverVerificationKey] = message;
        }
        if (!Utilities.IsNullOrUndefinedOrEmptyObject(verification.SubPropertyErrors))
        {
            for (const field of Object.keys(verification.SubPropertyErrors))
            {
                if (Utilities.IsNullOrUndefined(model[field])) continue;
                if (Array.isArray(model[field]))
                {
                    for (let i = 0; i < verification.SubPropertyErrors[field].length; i++)
                    {
                        const subVerification = verification.SubPropertyErrors[field][i];
                        const child = model[field][subVerification.Index];
                        if (child)
                            this.updateModel(child, subVerification);
                    }
                }
                else
                {
                    this.updateModel(model[field], verification.SubPropertyErrors[field][0]);
                }
            }
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    consolidateErrors(
        fieldName: string,
        model: AugmentedModelObject,
        errorMessages: ErrorMessage[]
    ): void
    {
        if (model && model.$errors && model.$errors[fieldName])
        {
            for (const errorName of Object.keys(model.$errors[fieldName]))
            {
                let errorMessage: ErrorMessage = ArrayHelpers.first(errorMessages, x => x.key === errorName);
                if (!errorMessage)
                {
                    errorMessage = {
                        key: errorName,
                        value: undefined,
                    };
                    errorMessages.push(errorMessage);
                }
                errorMessage.value = model.$errors[fieldName][errorName];
            }
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    clearError(control: AbstractControl, model: AugmentedModelObject, fieldName: string): void
    {
        control.errors[serverVerificationKey] = false;
        if (model && model.$errors && fieldName in model.$errors)
        {
            delete model.$errors[fieldName][serverVerificationKey];
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    viewHasErrors(view: IViewMetadata, value: AugmentedModelObject): boolean
    {
        if (!value)
            return false;
        const fieldNames: string[] = value.$errors ? Object.keys(value.$errors) : undefined;
        if (!Utilities.IsNullOrUndefinedOrEmpty(view?.ViewMetaData?.Properties))
            return this.propertiesHaveError(view.ViewMetaData.Properties, value, fieldNames);
        return false;
    }

    @LogInvocation(LogLevel.DEBUG)
    protected propertiesHaveError(properties: AugmentedPropertyMetadata[], value: AugmentedModelObject, fieldNames: string[]): boolean
    {
        return properties.some(property =>
        {
            if (property.ClassMetadata && property.ClassMetadata?.Properties)
                return this.propertiesHaveError(property.ClassMetadata.Properties, value, fieldNames);
            if (property.$panelGridControlErrors && !Utilities.IsNullOrUndefinedOrEmptyObject(property.$panelGridControlErrors))
                return true;
            if (!Utilities.IsNullOrUndefinedOrEmpty(property["$$formControlError"]) && property["$$touched"])
                return true;
            if (fieldNames && fieldNames.indexOf(property.Name) > -1)
                return this.hasErrors(value);
        });
    }

    @LogInvocation(LogLevel.DEBUG)
    hasErrors(model: AugmentedModelObject): boolean
    {
        if (!model?.$errors)
            return false;

        for (const fieldName of Object.keys(model.$errors))
        {
            if (Object.keys(model.$errors[fieldName]).length > 0)
                return true;
        }
        return false;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    getViewMetadataErrors(view: IViewMetadata): string[]
    {
        const errors: string[] = [];
        if (this.$metadataViewService.IsCombinedView(view))
        {
            view.Views.forEach(v =>
            {
                errors.push(...this.getViewMetadataErrors(v));
            });
        }
        else
        {
            const classMetadata = this.$metadataViewService.IsGrid(view)
                ? view.GridProperties.ClassMetadata
                : view.ViewMetaData;
            if (classMetadata && classMetadata.Properties)
                this.gatherPropertyErrors(classMetadata.Properties, errors);
        }
        return errors;
    }

    gatherPropertyErrors(properties: AugmentedPropertyMetadata[], errors: string[]): void
    {
        properties.forEach(p =>
        {
            if (p.ClassMetadata && p.ClassMetadata?.Properties)
            {
                this.gatherPropertyErrors(p.ClassMetadata.Properties, errors);
                return;
            }

            errors.push(...this.getErrorsForControl(p));
        });
    }

    getErrorsForControl(propertyMetadata: AugmentedPropertyMetadata): string[]
    {
        const errors: string[] = [];
        if (propertyMetadata.$panelGridControlErrors)
        {
            const indexes = Object.keys(propertyMetadata.$panelGridControlErrors);
            for (const index of indexes)
            {
                const msg = propertyMetadata.$panelGridControlErrors[index];
                if (msg && errors.indexOf(msg) < 0)
                    errors.push(msg);
            }
        }
        if (propertyMetadata["$$formControlError"] && errors.indexOf(propertyMetadata["$$formControlError"]) < 0)
            errors.push(propertyMetadata["$$formControlError"]);
        return errors;
    }

    getCellEditErrors(view: IViewMetadata, datacontroller: RepositoryDataCollection, model: any, action: IMetadataAction): string[]
    {
        const errors: string[] = [];
        if (this.$metadataViewService.IsCombinedView(view))
        {
            view.Views.forEach(v =>
            {
                errors.push(...this.getCellEditErrors(v, datacontroller, model, action));
            });
        }
        else
        {
            if (this.$metadataViewService.IsCellEditGrid(view))
            {
                let models: any[];
                if (view.SubPropertyName && !view.SubPropertyType && action.Context)
                    models = model[view.SubPropertyName];
                else if (view.SubPropertyName && !view.SubPropertyType && datacontroller.Entity)
                    models = datacontroller.Entity[view.SubPropertyName];
                else if (view.SubPropertyType && datacontroller.OtherEntities[view.SubPropertyType])
                {
                    models = datacontroller.OtherEntities[view.SubPropertyType];
                    if (view.SubPropertyName)
                        models = models[0][view.SubPropertyName];

                }
                else
                    models = datacontroller.OtherEntities[view["GridProperties"].ClassMetadata.TypeName];
                if (!Utilities.IsNullOrUndefinedOrEmpty(models))
                {
                    models.forEach(x =>
                    {
                        if (x.$errors)
                        {
                            Object.keys(x.$errors).forEach(property =>
                            {
                                const error = x.$errors[property].serverVerification;
                                if (errors.indexOf(error) === -1)
                                    errors.push(error);
                            });
                        }
                    });
                }
            }
        }
        return errors;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    showVerificationAlertDialog(errors: string[]): void
    {
        const listItems = errors.map((e) => `<li>${e}</li>`);
        const htmlMessage = `<ul>${listItems.join("")}</ul>`;
        this.$commonDialogsService.showAlertMessage(
            htmlMessage, "Verification Errors")
            .catch(_r =>
            {
                /* Squashing errors */
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    showErrorAlertDialog(view: IViewMetadata, model: AugmentedModelObject | AugmentedModelObject[]): void
    {
        const self = this;
        let properties = {};
        let htmlHeader = "";
        // errors need to be ordered to match the ordering of displayed fields.
        if (!Utilities.IsNullOrUndefined(view))
        {
            const classMetadata = this.$metadataViewService.IsGrid(view)
                ? view.GridProperties.ClassMetadata
                : view.ViewMetaData;
            properties = classMetadata.Properties.reduce((p, c) =>
            {
                p[c.Name] = c.RenderPosition;
                return p;
            }, {});
            htmlHeader = `<h3>${view.Title}</h3>`;
        }

        const htmlMessage = Array.isArray(model)
            ? formatListErrors(model)
            : formatModelErrors(model);

        this.$commonDialogsService.showAlertMessage(
            `${htmlHeader}${htmlMessage}`, "Verification Errors")
            .catch(_r =>
            {
                /* Squashing errors */
            });

        function formatListErrors(x: AugmentedModelObject[]): string
        {
            const listItems = x.reduce((p, c) =>
            {
                const errors = formatModelErrors(c) || "No errors";
                return `${p}<li><ul>${errors}</ul></li>`;
            }, "");
            return `<ol>${listItems}</ol>`;
        }
        function formatModelErrors(x: AugmentedModelObject): string
        {
            const fields = Object.keys(x.$errors || {});
            const modelErrors = fields.map(f => x.$errors[f].serverVerification);
            modelErrors.push(...formatControlErrors(view, x));
            return modelErrors.filter((e, i) => !!e && modelErrors.indexOf(e) === i)
                .reduce((p, e) => `${p}<li>${e}</li>`, "");

            function formatControlErrors(view: IViewMetadata, model: AugmentedModelObject): string[]
            {
                const properties = self.$metadataService.FlattenProperties(view.ViewMetaData, model.$$Parent, model);
                const errors = [];
                properties.forEach(p => errors.push(...self.getErrorsForControl((p.property as AugmentedPropertyMetadata))));
                return errors;
            }
        }
    }

    touchAndGetFormErrors(controls: { [key: string]: AbstractControl<any, any>; }, views: IViewMetadata[]): string[]
    {
        this.$metadataService.touchForm(controls);
        return this.FlattenFormErrors(views);
    }

    FlattenFormErrors(views: IViewMetadata[]): string[]
    {
        const errors: string[] = [];
        views.forEach(v =>
        {
            if (this.$metadataViewService.IsCombinedView(v))
            {
                errors.push(...this.FlattenFormErrors(v.Views));
            }
            else if (v.ViewMetaData?.Properties)
            {
                errors.push(...this.flattenPropertiesOnly(v.ViewMetaData));
            }
            else if (this.$metadataViewService.IsMetadataPanelGrid(v))
            {
                errors.push(...this.flattenPropertiesOnly(v.GridProperties.ClassMetadata));
            }
        });
        // Filter out empty errors and remove duplicates when same field appears in multiple steps
        const nonEmptyErrors = errors.filter(x => !Utilities.IsNullOrUndefinedOrEmpty(x));
        return nonEmptyErrors.filter((error, index) => nonEmptyErrors.indexOf(error) === index);
    }

    private flattenPropertiesOnly(metadata: ClassMetadata): string[]
    {
        return metadata.Properties.map((x: AugmentedPropertyMetadata) =>
        {
            let ret = [];
            if (x.ClassMetadata && !this.$metadataService.IsHiddenProperty(x))
                ret = this.flattenPropertiesOnly(x.ClassMetadata);
            else
            {
                ret = this.getErrorsForControl(x);
            }

            return ret;
        }).reduce((p, c) => p.concat(c), []);
    }
}
